import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Database, FileSearch, RotateCcw, ScanText, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";

const severityStyle = {
  high: "border-red-500/40 text-red-300 bg-red-500/5",
  medium: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  low: "border-sky-500/30 text-sky-300 bg-sky-500/5",
};

function detail(error, fallback) {
  const value = error?.response?.data?.detail;
  return typeof value === "string" ? value : fallback;
}

export default function ReviewPage() {
  const { t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [files, setFiles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [sourceMode, setSourceMode] = useState("upload");
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFileId, setExistingFileId] = useState("");
  const [dealId, setDealId] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [reviewItems, fileItems, dealItems] = await Promise.all([
        api.listReviews(), api.listFiles(), api.listDeals(),
      ]);
      setReviews(reviewItems);
      setFiles(fileItems);
      setDeals(dealItems);
      setActiveId((current) => current || reviewItems[0]?.id || null);
    } catch (error) {
      toast.error(detail(error, t("review_load_failed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const active = reviews.find((item) => item.id === activeId) || null;
  useEffect(() => {
    setSelectedSuggestionIds([]);
  }, [activeId, active?.deal_data_import?.extracted_at]);
  const visibleFindings = useMemo(
    () => (active?.findings || []).filter((item) => showAll || item.status === "open"),
    [active, showAll],
  );

  const runReview = async (event) => {
    event.preventDefault();
    setProcessing(true);
    try {
      let fileId = existingFileId;
      if (sourceMode === "upload") {
        if (!selectedFile) return;
        const data = new FormData();
        data.append("upload", selectedFile);
        data.append("set_name", t("review_upload_set"));
        data.append("document_type", t("review_document_type"));
        if (dealId) data.append("deal_id", dealId);
        const uploaded = await api.uploadFile(data);
        setFiles((items) => [uploaded, ...items]);
        fileId = uploaded.id;
      }
      if (!fileId) return;
      const created = await api.createReview({ file_id: fileId });
      setReviews((items) => [created, ...items]);
      setActiveId(created.id);
      setSelectedFile(null);
      setExistingFileId("");
      event.target.reset();
      toast.success(t("review_completed"));
    } catch (error) {
      toast.error(detail(error, t("review_failed")));
    } finally {
      setProcessing(false);
    }
  };

  const updateFinding = async (finding, status) => {
    try {
      const updated = await api.updateReviewFinding(active.id, finding.id, { status });
      setReviews((items) => items.map((item) => item.id === updated.id ? updated : item));
    } catch (error) {
      toast.error(detail(error, t("review_update_failed")));
    }
  };

  const extractDealData = async () => {
    setExtracting(true);
    try {
      const updated = await api.extractReviewDealData(active.id);
      setReviews((items) => items.map((item) => item.id === updated.id ? updated : item));
      toast.success(updated.deal_data_import?.suggestions?.length ? t("review_import_ready") : t("review_import_empty"));
    } catch (error) {
      toast.error(detail(error, t("review_import_failed")));
    } finally {
      setExtracting(false);
    }
  };

  const toggleSuggestion = (id) => {
    setSelectedSuggestionIds((items) => items.includes(id) ? items.filter((item) => item !== id) : [...items, id]);
  };

  const applyDealData = async () => {
    setApplying(true);
    try {
      const updated = await api.confirmReviewDealData(active.id, selectedSuggestionIds);
      setReviews((items) => items.map((item) => item.id === updated.id ? updated : item));
      setSelectedSuggestionIds([]);
      toast.success(t("review_import_applied"));
    } catch (error) {
      toast.error(detail(error, t("review_import_apply_failed")));
    } finally {
      setApplying(false);
    }
  };

  const removeReview = async (review) => {
    if (!window.confirm(t("review_delete_confirm"))) return;
    try {
      await api.deleteReview(review.id);
      const remaining = reviews.filter((item) => item.id !== review.id);
      setReviews(remaining);
      setActiveId(remaining[0]?.id || null);
      toast.success(t("review_deleted"));
    } catch (error) {
      toast.error(detail(error, t("review_delete_failed")));
    }
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-7xl" data-testid={TESTIDS.reviewPage}>
      <div className="mb-10">
        <div className="kicker mb-3">— {t("review_title")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-3">{t("review_title")}</h1>
        <p className="text-[var(--td-text-2)] max-w-3xl leading-relaxed">{t("review_intro")}</p>
      </div>

      <form onSubmit={runReview} data-testid={TESTIDS.reviewForm} className="card-surface p-4 sm:p-6 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <h2 className="font-serif text-2xl flex items-center gap-2"><FileSearch size={19} className="text-[var(--td-gold)]" />{t("review_new")}</h2>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {["upload", "existing"].map((mode) => (
              <button key={mode} type="button" onClick={() => setSourceMode(mode)}
                className={`font-mono text-[10px] uppercase tracking-widest border px-3 min-h-11 flex-1 sm:flex-none ${sourceMode === mode ? "border-[var(--td-gold)] text-[var(--td-gold)]" : "border-[var(--td-border)] text-[var(--td-text-3)]"}`}>
                {t(`review_source_${mode}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="grid lg:grid-cols-[minmax(260px,2fr)_minmax(200px,1fr)_auto] gap-3 items-end">
          {sourceMode === "upload" ? (
            <div>
              <label className="td-label">{t("review_file")}</label>
              <input required type="file" data-testid={TESTIDS.reviewFile}
                accept=".pdf,.docx,.txt,.csv,.eml,.png,.jpg,.jpeg,.tif,.tiff,.doc,.xls,.xlsx,.msg"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                className="td-input !py-2 text-xs file:mr-3 file:border-0 file:bg-transparent file:text-[var(--td-gold)]" />
            </div>
          ) : (
            <div>
              <label className="td-label">{t("review_existing_file")}</label>
              <select required className="td-input" data-testid={TESTIDS.reviewExisting}
                value={existingFileId} onChange={(event) => setExistingFileId(event.target.value)}>
                <option value="">{t("review_choose_file")}</option>
                {files.map((file) => <option key={file.id} value={file.id}>{file.display_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="td-label">{t("review_deal")}</label>
            <select className="td-input" value={dealId} disabled={sourceMode === "existing"}
              onChange={(event) => setDealId(event.target.value)}>
              <option value="">{t("review_no_deal")}</option>
              {deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.reference || deal.title}</option>)}
            </select>
          </div>
          <button type="submit" disabled={processing || (sourceMode === "upload" ? !selectedFile : !existingFileId)} data-testid={TESTIDS.reviewRun}
            className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap">
            {processing ? <RotateCcw size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
            {processing ? t("review_processing") : t("review_run")}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-[var(--td-text-3)]">{t("review_scope_hint")}</p>
      </form>

      {loading ? <div className="card-surface p-8 text-[var(--td-text-3)]">…</div> : reviews.length === 0 ? (
        <div className="card-surface p-14 text-center">
          <FileSearch size={32} className="mx-auto text-[var(--td-gold)] mb-3" />
          <h2 className="font-serif text-2xl">{t("review_empty")}</h2>
          <p className="text-sm text-[var(--td-text-3)] mt-2">{t("review_empty_hint")}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-6 items-start">
          <aside className="card-surface overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--td-border)] font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">{t("review_history")}</div>
            {reviews.map((review) => (
              <button key={review.id} type="button" onClick={() => setActiveId(review.id)} data-testid={TESTIDS.reviewRow}
                className={`w-full text-left p-4 border-b border-[var(--td-border)] last:border-b-0 transition-colors ${activeId === review.id ? "bg-[var(--td-gold)]/5 border-l-2 !border-l-[var(--td-gold)]" : "hover:bg-white/[0.02]"}`}>
                <div className="font-serif truncate">{review.file_name}</div>
                <div className="flex items-center justify-between mt-2 text-[10px] font-mono uppercase tracking-wider">
                  <span className={review.status === "ready" ? "text-emerald-400" : "text-amber-400"}>{t(`review_status_${review.status}`)}</span>
                  <span className="text-[var(--td-text-3)]">{review.metrics.open} {t("review_open_short")}</span>
                </div>
              </button>
            ))}
          </aside>

          {active && (
            <section>
              <div className="card-surface p-4 sm:p-6 mb-5">
                <div className="flex items-start justify-between gap-4 mb-5 min-w-0">
                  <div>
                    <div className="kicker mb-2">— {t("review_report")}</div>
                    <h2 className="font-serif text-2xl sm:text-3xl break-words">{active.file_name}</h2>
                    <div className="text-xs text-[var(--td-text-3)] mt-1">{new Date(active.created_at).toLocaleString()}</div>
                  </div>
                  <button type="button" onClick={() => removeReview(active)} aria-label={t("review_delete")} className="text-[var(--td-text-3)] hover:text-red-400"><Trash2 size={16} /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-5">
                  <Metric label={t("review_words")} value={active.metrics.word_count} />
                  <Metric label={t("review_high")} value={active.metrics.high} tone="text-red-300" />
                  <Metric label={t("review_medium")} value={active.metrics.medium} tone="text-amber-300" />
                  <Metric label={t("review_low")} value={active.metrics.low} tone="text-sky-300" />
                  <Metric label={t("review_open")} value={active.metrics.open} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] mb-2">{t("review_summary")}</div>
                <p className="text-sm leading-relaxed text-[var(--td-text-2)]">{active.summary}</p>
              </div>

              <div className="card-surface p-4 sm:p-6 mb-5" data-testid="deal-data-import">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="kicker mb-2">— {t("review_import_kicker")}</div>
                    <h3 className="font-serif text-2xl flex items-center gap-2"><Database size={18} className="text-[var(--td-gold)]" />{t("review_import_title")}</h3>
                    <p className="text-sm text-[var(--td-text-3)] mt-2 max-w-2xl">{t("review_import_intro")}</p>
                  </div>
                  <button type="button" onClick={extractDealData} disabled={extracting || !active.deal_id}
                    className="border border-[var(--td-border)] px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] inline-flex items-center justify-center gap-2 disabled:opacity-40 whitespace-nowrap min-h-11">
                    {extracting ? <RotateCcw size={14} className="animate-spin" /> : <ScanText size={15} />}
                    {extracting ? t("review_import_extracting") : t("review_import_extract")}
                  </button>
                </div>

                {!active.deal_id && <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">{t("review_import_needs_deal")}</div>}

                {active.deal_data_import?.status === "empty" && (
                  <div className="mt-4 border border-[var(--td-border)] p-4 text-sm text-[var(--td-text-3)]">{t("review_import_empty")}</div>
                )}

                {active.deal_data_import?.suggestions?.length > 0 && (
                  <div className="mt-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] mb-3">
                      {active.deal_data_import.status === "applied" ? t("review_import_applied_title") : t("review_import_choose")}
                    </div>
                    <div className="space-y-2">
                      {active.deal_data_import.suggestions.map((suggestion) => {
                        const applied = active.deal_data_import.applied_suggestion_ids?.includes(suggestion.id);
                        const pending = active.deal_data_import.status === "pending";
                        return (
                          <label key={suggestion.id} className={`block border p-3 sm:p-4 ${applied ? "border-emerald-500/40 bg-emerald-500/5" : "border-[var(--td-border)]"} ${pending ? "cursor-pointer hover:border-[var(--td-gold)]/50" : ""}`}>
                            <div className="flex items-start gap-3">
                              {pending ? <input type="checkbox" checked={selectedSuggestionIds.includes(suggestion.id)} onChange={() => toggleSuggestion(suggestion.id)} className="mt-1 accent-[var(--td-gold)]" /> : <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${applied ? "text-emerald-400" : "text-[var(--td-text-3)]"}`} />}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium">{suggestion.label}</span>
                                  <span className="font-mono text-[9px] uppercase tracking-widest border border-[var(--td-border)] px-1.5 py-0.5">{t(`review_import_confidence_${suggestion.confidence}`)}</span>
                                </div>
                                <div className="mt-1 text-sm text-[var(--td-gold)] break-words">{suggestion.value}</div>
                                {suggestion.evidence && <div className="mt-1 text-xs italic text-[var(--td-text-3)] break-words">“{suggestion.evidence}”</div>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {active.deal_data_import.status === "pending" && (
                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-[11px] text-[var(--td-text-3)]">{t("review_import_confirm_hint")}</p>
                        <button type="button" onClick={applyDealData} disabled={applying || selectedSuggestionIds.length === 0}
                          className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-40 whitespace-nowrap min-h-11">
                          {applying ? <RotateCcw size={14} className="animate-spin" /> : <Check size={15} />}
                          {applying ? t("review_import_applying") : `${t("review_import_apply")} (${selectedSuggestionIds.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                <h3 className="font-serif text-2xl">{t("review_findings")}</h3>
                <button type="button" onClick={() => setShowAll((value) => !value)} className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)]">
                  {showAll ? t("review_show_open") : t("review_show_all")}
                </button>
              </div>
              {visibleFindings.length === 0 ? (
                <div className="card-surface p-10 text-center text-emerald-300"><CheckCircle2 size={24} className="mx-auto mb-2" />{t("review_no_open")}</div>
              ) : (
                <div className="space-y-3">
                  {visibleFindings.map((finding) => (
                    <article key={finding.id} data-testid={TESTIDS.reviewFinding} className={`border p-4 sm:p-5 ${severityStyle[finding.severity] || severityStyle.low} ${finding.status !== "open" ? "opacity-60" : ""}`}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={17} className="mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-mono text-[9px] uppercase tracking-widest border border-current px-1.5 py-0.5">{t(`review_severity_${finding.severity}`)}</span>
                            <span className="font-mono text-[9px] uppercase tracking-widest opacity-70">{finding.category.replace("_", " ")}</span>
                            {finding.status !== "open" && <span className="font-mono text-[9px] uppercase tracking-widest">{t(`review_finding_${finding.status}`)}</span>}
                          </div>
                          <h4 className="font-serif text-xl mb-2">{finding.title}</h4>
                          <p className="text-sm leading-relaxed opacity-90">{finding.description}</p>
                          <div className="mt-3 border-l-2 border-current/30 pl-3 text-xs italic opacity-75">{finding.evidence}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {finding.status === "open" ? <>
                            <button type="button" onClick={() => updateFinding(finding, "resolved")} title={t("review_resolve")} className="hover:text-emerald-300"><Check size={16} /></button>
                            <button type="button" onClick={() => updateFinding(finding, "accepted")} title={t("review_accept")} className="font-mono text-[10px] uppercase">{t("review_accept_short")}</button>
                          </> : (
                            <button type="button" onClick={() => updateFinding(finding, "open")} title={t("review_reopen")}><RotateCcw size={15} /></button>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              <p className="mt-5 text-[11px] text-[var(--td-text-3)]">{t("review_disclaimer")}</p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = "" }) {
  return <div className="border border-[var(--td-border)] p-3"><div className={`font-serif text-2xl ${tone}`}>{value ?? "—"}</div><div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)] mt-1">{label}</div></div>;
}
